import { useMemo } from "react"

import { SelectOption } from "shared/types/Select"

/**
 * @template T - Type of the option value.
 * @interface UseFirstSelectedOptionArgs - Arguments for the useFirstSelectedOption hook.
 */
interface UseFirstSelectedOptionArgs<T> {
  /**
   * An array of available options.
   */
  options: SelectOption<T>[] | null | undefined
  /**
   * The currently selected option(s).
   */
  selectedOptions: T | T[] | null | undefined
}

/**
 * Custom hook that returns the index of the first selected option in an array of options.
 *
 * @template T - The type of the option values.
 * @param options - An array of available options.
 * @param selectedOptions - The currently selected option(s).
 * @returns The index of the first selected option, or undefined if no option is selected.
 */
export function useFirstSelectedOption<T>({
  options,
  selectedOptions
}: UseFirstSelectedOptionArgs<T>) {
  return useMemo(() => {
    let index: number | undefined
    if (Array.isArray(selectedOptions)) {
      index = options?.findIndex(option =>
        selectedOptions.includes(option.value)
      )
    } else {
      index = options?.findIndex(option => option.value === selectedOptions)
    }
    return index === -1 ? undefined : index
  }, [options, selectedOptions])
}
